import React, { useState, useEffect } from "react";
import { Star, MessageSquare, CheckCircle, Clock } from "lucide-react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  createPerformanceReview,
  fetchPerformanceReviewsByTeacher,
  updateAdminComments,
  updateEmployeeComments,
  updateObjectiveEmployeeComment,
  updateObjectiveScore,
  clearError,
  resetReviews,
} from "../../../redux/performanceReviewSlice";

const PerformanceReview = ({ teacherEmail, isAdmin = false, isModalOpen = true }) => {
  const dispatch = useDispatch();
  const { reviews, loading, error } = useSelector((state) => state.performanceReview);

  const [activeReview, setActiveReview] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [objectiveComments, setObjectiveComments] = useState({});
  const [objectiveScores, setObjectiveScores] = useState({});

  // Fetch reviews when component mounts or teacherEmail/modal opens
  useEffect(() => {
    if (teacherEmail && isModalOpen) {
      dispatch(resetReviews());
      dispatch(fetchPerformanceReviewsByTeacher(teacherEmail));
    }
  }, [dispatch, teacherEmail, isModalOpen]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleInitiateReview = async (type) => {
    if (isAdmin && teacherEmail) {
      try {
        const reviewData = {
          teacherEmail: teacherEmail,
          type: type,
          performanceYear: new Date().getFullYear(),
          initiatedDate: new Date().toISOString(),
        };
        await dispatch(createPerformanceReview(reviewData)).unwrap();
        toast.success(`${type === "midTerm" ? "Mid-term" : "Final Year"} review initiated`);
        // Refresh reviews
        dispatch(fetchPerformanceReviewsByTeacher(teacherEmail));
      } catch (err) {
        toast.error(err || "Failed to initiate review");
      }
    }
  };

  const handleSaveAdminComment = async (reviewId) => {
    if (!commentText.trim()) {
      toast.error("Please enter a comment");
      return;
    }
    try {
      await dispatch(
        updateAdminComments({
          reviewId,
          adminComments: commentText,
        })
      ).unwrap();
      setCommentText("");
      toast.success("Comment saved successfully");
      // Refresh reviews
      dispatch(fetchPerformanceReviewsByTeacher(teacherEmail));
    } catch (err) {
      toast.error(err || "Failed to save comment");
    }
  };

  const handleSaveEmployeeComment = async (reviewId) => {
    if (!commentText.trim()) {
      toast.error("Please enter a comment");
      return;
    }
    try {
      await dispatch(
        updateEmployeeComments({
          reviewId,
          employeeComments: commentText,
        })
      ).unwrap();
      setCommentText("");
      toast.success("Comment saved successfully");
      // Refresh reviews
      dispatch(fetchPerformanceReviewsByTeacher(teacherEmail));
    } catch (err) {
      toast.error(err || "Failed to save comment");
    }
  };

  const handleSaveObjectiveComment = async (reviewId, objectiveId, comment) => {
    if (!comment || !comment.trim()) {
      toast.error("Please enter a comment");
      return;
    }
    try {
      await dispatch(
        updateObjectiveEmployeeComment({
          reviewId,
          objectiveId,
          employeeComments: comment,
        })
      ).unwrap();
      setObjectiveComments({ ...objectiveComments, [`${reviewId}-${objectiveId}`]: "" });
      toast.success("Objective comment saved");
      // Refresh reviews
      dispatch(fetchPerformanceReviewsByTeacher(teacherEmail));
    } catch (err) {
      toast.error(err || "Failed to save objective comment");
    }
  };

  const handleSaveObjectiveScore = async (reviewId, objectiveId, score, comment) => {
    if (!score) {
      toast.error("Please select a score");
      return;
    }
    try {
      await dispatch(
        updateObjectiveScore({
          reviewId,
          objectiveId,
          adminScore: score,
          adminComments: comment || "",
        })
      ).unwrap();
      setObjectiveScores({ ...objectiveScores, [`${reviewId}-${objectiveId}`]: "" });
      setObjectiveComments({ ...objectiveComments, [`${reviewId}-${objectiveId}`]: "" });
      toast.success("Score and comment saved");
      // Refresh reviews
      dispatch(fetchPerformanceReviewsByTeacher(teacherEmail));
    } catch (err) {
      toast.error(err || "Failed to save score");
    }
  };

  const midTermReviews = (reviews || []).filter((r) => r.type === "midTerm");
  const finalYearReviews = (reviews || []).filter((r) => r.type === "finalYear");

  if (loading && reviews.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>Loading performance reviews...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Admin Actions */}
      {isAdmin && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 mb-3">Admin Actions</h3>
          <div className="flex gap-3">
            <button
              onClick={() => handleInitiateReview("midTerm")}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              disabled={loading}
            >
              Initiate Mid-term Review
            </button>
            <button
              onClick={() => handleInitiateReview("finalYear")}
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
              disabled={loading}
            >
              Initiate Final Year Review
            </button>
          </div>
        </div>
      )}

      {/* Mid-term Reviews */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Clock className="text-blue-600" size={20} />
          Mid-term Reviews
        </h3>
        {midTermReviews.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <p>No mid-term reviews initiated yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {midTermReviews.map((review) => (
              <div
                key={review.id}
                className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-gray-700">
                        Performance Year: {review.performanceYear}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          review.status === "completed"
                            ? "bg-green-200 text-green-800"
                            : "bg-yellow-200 text-yellow-800"
                        }`}
                      >
                        {review.status === "completed" ? "Closed" : "In Progress"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Initiated: {new Date(review.initiatedDate).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setActiveReview(activeReview === review.id ? null : review.id)
                    }
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {activeReview === review.id ? "Hide Details" : "Show Details"}
                  </button>
                </div>

                {activeReview === review.id && (
                  <div className="space-y-4 mt-4">
                    {/* Admin Comments Section */}
                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        {isAdmin ? "Your Comments" : "Admin/Principal Comments"}
                      </h4>
                      {review.adminComments ? (
                        <p className="text-sm text-gray-700 mb-2">
                          {review.adminComments}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 italic mb-2">
                          No comments yet
                        </p>
                      )}
                      {isAdmin && (
                        <div className="mt-2">
                          <textarea
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            rows="3"
                            placeholder="Add your comments..."
                          />
                          <button
                            onClick={() => handleSaveAdminComment(review.id)}
                            className="mt-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                            disabled={loading}
                          >
                            Save Comment
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Employee Comments Section */}
                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        {isAdmin ? "Employee Comments" : "Your Comments"}
                      </h4>
                      {review.employeeComments ? (
                        <p className="text-sm text-gray-700 mb-2">
                          {review.employeeComments}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 italic mb-2">
                          No comments yet
                        </p>
                      )}
                      {!isAdmin && (
                        <div className="mt-2">
                          {review.status === "completed" ? (
                            <p className="text-sm text-gray-500 italic">This review is closed. You cannot add or edit comments.</p>
                          ) : (
                            <>
                              <textarea
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                rows="3"
                                placeholder="Add your comments..."
                              />
                              <button
                                onClick={() => handleSaveEmployeeComment(review.id)}
                                className="mt-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                                disabled={loading}
                              >
                                Save Comment
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Objectives Review */}
                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-3">
                        Objectives Review
                      </h4>
                      <div className="space-y-3">
                        {(review.objectives || []).map((obj) => (
                          <div key={obj.objectiveId} className="border-b pb-3 last:border-b-0">
                            <p className="font-medium text-sm text-gray-700 mb-2">
                              {obj.objective}
                            </p>
                            {obj.adminComments && (
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-gray-600">
                                  Admin Comments:
                                </p>
                                <p className="text-sm text-gray-700">{obj.adminComments}</p>
                              </div>
                            )}
                            {obj.employeeComments && (
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-gray-600">
                                  {isAdmin ? "Employee Comments:" : "Your Comments:"}
                                </p>
                                <p className="text-sm text-gray-700">{obj.employeeComments}</p>
                              </div>
                            )}
                            {!isAdmin && (
                              <div className="mt-2">
                                {review.status === "completed" ? (
                                  <p className="text-xs text-gray-500 italic">This review is closed.</p>
                                ) : (
                                  <>
                                    <textarea
                                      value={
                                        objectiveComments[`${review.id}-${obj.objectiveId}`] ||
                                        obj.employeeComments ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        setObjectiveComments({
                                          ...objectiveComments,
                                          [`${review.id}-${obj.objectiveId}`]: e.target.value,
                                        })
                                      }
                                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                      rows="2"
                                      placeholder="Add your comments on this objective..."
                                    />
                                    <button
                                      onClick={() =>
                                        handleSaveObjectiveComment(
                                          review.id,
                                          obj.objectiveId,
                                          objectiveComments[`${review.id}-${obj.objectiveId}`] ||
                                            obj.employeeComments ||
                                            ""
                                        )
                                      }
                                      className="mt-1 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs"
                                      disabled={loading}
                                    >
                                      Save Comment
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Final Year Reviews */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Star className="text-purple-600" size={20} />
          Final Year Reviews
        </h3>
        {finalYearReviews.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <p>No final year reviews initiated yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {finalYearReviews.map((review) => (
              <div
                key={review.id}
                className="border-2 border-purple-300 rounded-lg p-4 bg-purple-50"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-gray-700">
                        Performance Year: {review.performanceYear}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          review.status === "completed"
                            ? "bg-green-200 text-green-800"
                            : "bg-yellow-200 text-yellow-800"
                        }`}
                      >
                        {review.status === "completed" ? "Closed" : "In Progress"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Initiated: {new Date(review.initiatedDate).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setActiveReview(activeReview === review.id ? null : review.id)
                    }
                    className="text-purple-600 hover:text-purple-800"
                  >
                    {activeReview === review.id ? "Hide Details" : "Show Details"}
                  </button>
                </div>

                {activeReview === review.id && (
                  <div className="space-y-4 mt-4">
                    {/* Admin Comments Section */}
                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        {isAdmin ? "Your Comments" : "Admin/Principal Comments"}
                      </h4>
                      {review.adminComments ? (
                        <p className="text-sm text-gray-700 mb-2">
                          {review.adminComments}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 italic mb-2">
                          No comments yet
                        </p>
                      )}
                      {isAdmin && (
                        <div className="mt-2">
                          <textarea
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            rows="3"
                            placeholder="Add your comments after discussion..."
                          />
                          <button
                            onClick={() => handleSaveAdminComment(review.id)}
                            className="mt-2 bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm"
                            disabled={loading}
                          >
                            Save Comment
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Employee Comments Section */}
                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        {isAdmin ? "Employee Comments" : "Your Comments"}
                      </h4>
                      {review.employeeComments ? (
                        <p className="text-sm text-gray-700 mb-2">
                          {review.employeeComments}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 italic mb-2">
                          No comments yet
                        </p>
                      )}
                      {!isAdmin && (
                        <div className="mt-2">
                          {review.status === "completed" ? (
                            <p className="text-sm text-gray-500 italic">This review is closed. You cannot add or edit comments.</p>
                          ) : (
                            <>
                              <textarea
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                rows="3"
                                placeholder="Add your comments..."
                              />
                              <button
                                onClick={() => handleSaveEmployeeComment(review.id)}
                                className="mt-2 bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm"
                                disabled={loading}
                              >
                                Save Comment
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Objectives with Scores (Final Year Only) */}
                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-semibold text-gray-800 mb-3">
                        Objectives Review & Scoring
                      </h4>
                      <div className="space-y-4">
                        {(review.objectives || []).map((obj) => (
                          <div
                            key={obj.objectiveId}
                            className="border-2 border-gray-200 rounded p-4"
                          >
                            <p className="font-medium text-sm text-gray-700 mb-3">
                              {obj.objective}
                            </p>

                            {/* Admin can add score and comments */}
                            {isAdmin && (
                              <div className="space-y-3 mb-3">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                                    Final Score (1-5)
                                  </label>
                                  <div className="flex gap-2">
                                    {[1, 2, 3, 4, 5].map((score) => (
                                      <button
                                        key={score}
                                        onClick={() =>
                                          setObjectiveScores({
                                            ...objectiveScores,
                                            [`${review.id}-${obj.objectiveId}`]: score,
                                          })
                                        }
                                        className={`w-10 h-10 rounded flex items-center justify-center ${
                                          objectiveScores[`${review.id}-${obj.objectiveId}`] ===
                                          score || obj.adminScore === score
                                            ? "bg-purple-600 text-white"
                                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                        }`}
                                      >
                                        {score}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                                    Your Comments
                                  </label>
                                  <textarea
                                    value={
                                      objectiveComments[
                                        `${review.id}-${obj.objectiveId}`
                                      ] || obj.adminComments || ""
                                    }
                                    onChange={(e) =>
                                      setObjectiveComments({
                                        ...objectiveComments,
                                        [`${review.id}-${obj.objectiveId}`]: e.target.value,
                                      })
                                    }
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                    rows="2"
                                    placeholder="Add your comments and final score..."
                                  />
                                </div>
                                <button
                                  onClick={() =>
                                    handleSaveObjectiveScore(
                                      review.id,
                                      obj.objectiveId,
                                      objectiveScores[`${review.id}-${obj.objectiveId}`] ||
                                        obj.adminScore,
                                      objectiveComments[`${review.id}-${obj.objectiveId}`] ||
                                        obj.adminComments ||
                                        ""
                                    )
                                  }
                                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm"
                                  disabled={loading}
                                >
                                  Save Score & Comment
                                </button>
                              </div>
                            )}

                            {/* Display score if set */}
                            {obj.adminScore && (
                              <div className="mb-3">
                                <p className="text-xs font-semibold text-gray-600 mb-1">
                                  Final Score:
                                </p>
                                <div className="flex gap-1">
                                  {[1, 2, 3, 4, 5].map((score) => (
                                    <Star
                                      key={score}
                                      size={20}
                                      className={
                                        score <= obj.adminScore
                                          ? "text-yellow-500 fill-yellow-500"
                                          : "text-gray-300"
                                      }
                                    />
                                  ))}
                                  <span className="ml-2 text-sm font-semibold">
                                    {obj.adminScore}/5
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Display admin comments */}
                            {obj.adminComments && (
                              <div className="mb-3">
                                <p className="text-xs font-semibold text-gray-600 mb-1">
                                  Admin Comments:
                                </p>
                                <p className="text-sm text-gray-700">{obj.adminComments}</p>
                              </div>
                            )}

                            {/* Employee can add comments */}
                            {!isAdmin && (
                              <div className="mt-3">
                                {review.status === "completed" ? (
                                  <p className="text-xs text-gray-500 italic">This review is closed.</p>
                                ) : (
                                  <>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                                      Your Comments
                                    </label>
                                    <textarea
                                      value={
                                        objectiveComments[`${review.id}-${obj.objectiveId}`] ||
                                        obj.employeeComments ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        setObjectiveComments({
                                          ...objectiveComments,
                                          [`${review.id}-${obj.objectiveId}`]: e.target.value,
                                        })
                                      }
                                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                      rows="2"
                                      placeholder="Add your comments on this objective..."
                                    />
                                    <button
                                      onClick={() =>
                                        handleSaveObjectiveComment(
                                          review.id,
                                          obj.objectiveId,
                                          objectiveComments[`${review.id}-${obj.objectiveId}`] ||
                                            obj.employeeComments ||
                                            ""
                                        )
                                      }
                                      className="mt-2 bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 text-xs"
                                      disabled={loading}
                                    >
                                      Save Comment
                                    </button>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Display employee comments */}
                            {obj.employeeComments && (
                              <div className="mt-3">
                                <p className="text-xs font-semibold text-gray-600 mb-1">
                                  {isAdmin ? "Employee Comments:" : "Your Comments:"}
                                </p>
                                <p className="text-sm text-gray-700">{obj.employeeComments}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceReview;
